import {
  Alert,
  Box,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SidePanel } from '../../components/layout/SidePanel';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../components/shared/ConfirmDialog';
import { EditableGrid } from '../../components/tables/EditableGrid';
import { ROUTES } from '../../constants/routes';
import { getLocationsByProject } from '../../services/locationService';
import { updateProject } from '../../services/projectService';
import {
  buildSampleCascadeCopy,
  bulkUpdateField,
  createSample,
  deleteSamples,
  getSamplesByLocation,
  updateSample,
} from '../../services/sampleService';
import type { Location } from '../../types/location';
import type { Project } from '../../types/project';
import { NewLocationDialog } from '../locations/NewLocationDialog';
import { CsvUploadModal } from '../locations/CsvUploadModal';
import type { NormalizedSampleRow } from '../locations/csvUploadValidation';
import { describeImportOutcome, importSamplesAcrossLocations } from '../locations/sampleCsvImport';
import { SampleActionBar } from '../locations/SampleActionBar';
import { sampleColumns } from '../locations/sampleColumns';
import { sampleColumnsForUnit, toDisplayDepth, toMeters, type DepthUnit } from '../locations/sampleColumnsForUnit';
import { BulkUpdateBar } from '../locations/BulkUpdateBar';
import { ColumnSelector } from '../../components/tables/ColumnSelector';
import { useColumnPreferences } from '../../hooks/useColumnPreferences';
import { usePermissions } from '../../hooks/usePermissions';

interface LocationsTabContentProps {
  project: Project;
  projectId: string;
}

export const LocationsTabContent = ({ project, projectId }: LocationsTabContentProps) => {
  const navigate = useNavigate();
  const { canManageEntities } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationKey, setSelectedLocationKey] = useState<string | null>(null);
  const [samples, setSamples] = useState<Record<string, unknown>[]>([]);
  const [selectedSampleKeys, setSelectedSampleKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  // TASK-005 / BUG-2026-0170: the depth unit is a PROJECT fact
  // (Project.ReportUnit), answered at CSV import — label-only, no conversion.
  const [unit, setUnit] = useState<DepthUnit>(project.ReportUnit === 'imperial' ? 'ft' : 'm');

  useEffect(() => {
    setUnit(project.ReportUnit === 'imperial' ? 'ft' : 'm');
  }, [project.ReportUnit]);
  const [highlightSample, setHighlightSample] = useState<string | null>(searchParams.get('sample'));
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({
    open: false, message: '', severity: 'success',
  });
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  // Clear highlight after 1.5 seconds
  useEffect(() => {
    if (!highlightSample) return;
    const t = setTimeout(() => setHighlightSample(null), 1500);
    return () => clearTimeout(t);
  }, [highlightSample]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const locs = await getLocationsByProject(project.ProjectKey);
        setLocations(locs);
        if (locs.length > 0) {
          const urlLoc = searchParams.get('location');
          // Match by LocationKey (UUID) first, then fall back to LocationId
          const match = urlLoc && (
            locs.find((l) => String(l.LocationKey) === urlLoc) ||
            locs.find((l) => l.LocationId === urlLoc)
          );
          setSelectedLocationKey(match ? String(match.LocationKey) : String(locs[0].LocationKey));
        }
      } catch (err) {
        console.error('Failed to load locations:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [project.ProjectKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedLocationKey) { setSamples([]); return; }
    const loadSamples = async () => {
      try {
        const data = await getSamplesByLocation(selectedLocationKey);
        setSamples(data as unknown as Record<string, unknown>[]);
      } catch { setSamples([]); }
    };
    loadSamples();
  }, [selectedLocationKey]);

  const selectedLocation = locations.find((l) => String(l.LocationKey) === selectedLocationKey);

  const allFields = sampleColumns.map((c) => c.field);
  const { visibleFields, columnVisibility, toggleColumn, resetDefaults } = useColumnPreferences(
    'samples', sampleColumns, allFields, ['SampleId', 'DepthTop'],
  );
  const unitColumns = sampleColumnsForUnit(unit);
  const filteredColumns = unitColumns.filter((c) => visibleFields.includes(c.field));

  const displaySamples = samples.map((s) => ({
    ...s,
    // FEAT-2026-0018: surface the borehole / test-pit id on every sample row.
    // All rows in this grid belong to the selected location, so it's the same
    // value for each — already-loaded data, no extra fetch.
    LocationId: selectedLocation?.LocationId ?? '',
    DepthTop: toDisplayDepth(s.DepthTop, unit),
    DepthBase: toDisplayDepth(s.DepthBase, unit),
    __highlight: highlightSample && (String(s.SampleKey) === highlightSample || String(s.SampleId) === highlightSample),
  }));

  /** Persist the project's unit designation (label-only, never converts). */
  const persistUnit = useCallback(async (next: DepthUnit) => {
    await updateProject(project.ProjectKey, { ReportUnit: next === 'ft' ? 'imperial' : 'metric' } as never);
    setUnit(next);
  }, [project.ProjectKey]);

  const handleUnitChange = (next: DepthUnit) => {
    if (next === unit) return;
    setConfirmDlg({
      open: true,
      title: `Mark this project's depths as ${next === 'ft' ? 'feet' : 'metres'}?`,
      message: `This changes the unit LABEL for the whole project — grids and report PDFs will show depths in ${next === 'ft' ? 'ft' : 'm'}. The stored values are never converted, so only do this if the project's data really is in ${next === 'ft' ? 'feet' : 'metres'}.`,
      confirmLabel: 'Change unit',
      confirmColor: 'primary',
      onConfirm: async () => {
        try {
          await persistUnit(next);
          setSnackbar({ open: true, message: `Project depth unit set to ${next}`, severity: 'success' });
        } catch {
          setSnackbar({ open: true, message: 'Failed to update the project unit', severity: 'error' });
        }
      },
    });
  };

  const handleGenerateRefs = useCallback(async () => {
    if (!selectedLocation || !selectedLocationKey) return;
    // Need BOTH: no SampleId yet AND a real DepthTop value (not null, not '').
    // New in-memory rows default to DepthTop: '' which would otherwise produce
    // a broken "<LocationId>_" reference.
    const blanks = samples.filter((s) =>
      !String(s.SampleId || '').trim()
      && s.DepthTop != null
      && s.DepthTop !== ''
    );
    if (blanks.length === 0) { setSnackbar({ open: true, message: 'No samples need a reference', severity: 'success' }); return; }
    let updated = 0;
    for (const s of blanks) {
      const newRef = `${selectedLocation.LocationId}_${s.DepthTop}`;
      if (String(s.SampleKey).startsWith('new-')) {
        // Client-only row: fill the reference locally. Row stays unsaved —
        // user still needs to save it (or bulk-save).
        setSamples((prev) => prev.map((x) =>
          x.SampleKey === s.SampleKey ? { ...x, SampleId: newRef } : x,
        ));
        updated++;
      } else {
        try {
          await updateSample(String(s.SampleKey), { SampleId: newRef } as any);
          updated++;
        } catch { /* skip failures */ }
      }
    }
    // Refetch to pick up persisted server changes, but preserve any client-only
    // (new-*) rows that haven't been saved yet — the refetch only returns
    // server-side rows, so without this merge the unsaved new row would vanish
    // from the table and the user would see a phantom "saved" toast.
    const fresh = await getSamplesByLocation(selectedLocationKey);
    setSamples((prev) => {
      const clientOnly = prev.filter((s) => String(s.SampleKey).startsWith('new-'));
      return [...(fresh as unknown as Record<string, unknown>[]), ...clientOnly];
    });
    setSnackbar({ open: true, message: `Generated ${updated} sample reference(s)`, severity: 'success' });
  }, [samples, selectedLocation, selectedLocationKey]);

  const [undoSnapshot, setUndoSnapshot] = useState<{ rowIndex: number; row: Record<string, unknown> } | null>(null);

  const handleRowChange = useCallback((rowIndex: number, field: string, value: unknown) => {
    const stored = (field === 'DepthTop' || field === 'DepthBase') ? toMeters(value, unit) : value;
    setSamples((prev) => {
      setUndoSnapshot({ rowIndex, row: { ...prev[rowIndex] } });
      const next = [...prev];
      next[rowIndex] = { ...next[rowIndex], [field]: stored };
      return next;
    });
  }, []);

  const handleUndo = useCallback(() => {
    const lastNewIdx = samples.reduce((acc, s, i) => String(s.SampleKey).startsWith('new-') ? i : acc, -1);
    if (lastNewIdx !== -1) {
      setSamples((prev) => prev.filter((_, i) => i !== lastNewIdx));
      setUndoSnapshot(null);
      return;
    }
    if (!undoSnapshot) return;
    setSamples((prev) => {
      const next = [...prev];
      next[undoSnapshot.rowIndex] = undoSnapshot.row;
      return next;
    });
    setUndoSnapshot(null);
  }, [undoSnapshot, samples]);

  const handleRowSave = useCallback(async (row: Record<string, unknown>) => {
    if (!selectedLocationKey) return;
    if (!String(row.SampleId || '').trim()) {
      setSnackbar({ open: true, message: 'Sample ID is required', severity: 'error' }); return;
    }
    if (row.DepthTop == null || row.DepthTop === '' || isNaN(Number(row.DepthTop))) {
      setSnackbar({ open: true, message: 'Depth must be a valid number', severity: 'error' }); return;
    }
    if (!String(row.SampleType || '').trim()) {
      setSnackbar({ open: true, message: 'Sample Type is required', severity: 'error' }); return;
    }
    const key = row.SampleKey;
    try {
      if (key && !String(key).startsWith('new-')) {
        const updateData = { ...row, SampleReference: String(row.SampleId || '') } as any;
        await updateSample(String(key), updateData);
      } else if (row.SampleId) {
        const sampleId = String(row.SampleId);
        const created = await createSample(selectedLocationKey, {
          SampleId: sampleId, SampleReference: sampleId, SampleType: String(row.SampleType || ''),
          DepthTop: Number(row.DepthTop),
          DepthBase: row.DepthBase != null ? Number(row.DepthBase) : undefined,
          Status: String(row.Status || 'Received'), Condition: String(row.Condition || ''),
          SampleComments: String(row.SampleComments || ''), StorageLocation: String(row.StorageLocation || ''),
        });
        setSamples((prev) => prev.map((s) => s.SampleKey === row.SampleKey ? { ...s, SampleKey: created.SampleKey } : s));
      }
      setSnackbar({ open: true, message: 'Saved', severity: 'success' });
    } catch (err: any) {
      const apiMsg = err?.response?.data?.error || '';
      const msg = apiMsg.toLowerCase().includes('already exists')
        ? 'Failed to save sample. Duplicate Sample Reference.'
        : apiMsg || 'Failed to save sample';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  }, [selectedLocationKey]);

  const handleAddRow = () => {
    setSamples((prev) => [...prev, {
      SampleKey: `new-${Date.now()}`, SampleId: '', SampleType: '', DepthTop: '',
      Status: 'Received', Condition: '', SampleComments: '', BoxOrBucketNumber: '',
      StorageLocation: '', Priority: 'Normal',
    }]);
  };

  const handleBulkUpdate = async (field: string, value: string) => {
    const realKeys = selectedSampleKeys.filter((k) => !k.startsWith('new-'));
    if (realKeys.length === 0) { setSnackbar({ open: true, message: 'No saved samples selected', severity: 'error' }); return; }
    try {
      const result = await bulkUpdateField(realKeys, field, value);
      setSamples((prev) => prev.map((s) => selectedSampleKeys.includes(String(s.SampleKey)) ? { ...s, [field]: value } : s));
      setSnackbar({ open: true, message: `Updated ${result.updated} sample(s)`, severity: 'success' });
    } catch { setSnackbar({ open: true, message: 'Bulk update failed', severity: 'error' }); }
  };

  const handleDelete = async (keys: string[]) => {
    const realKeys = keys.filter((k) => !k.startsWith('new-'));
    const newKeys = keys.filter((k) => k.startsWith('new-'));
    try {
      // P2-10: samples with tests come back BLOCKED (409 SAMPLE_HAS_TESTS) -
      // confirm the cascade (sample + its tests) and retry with cascade=true.
      const result = realKeys.length > 0
        ? await deleteSamples(realKeys)
        : { deleted: [] as string[], blocked: [] };
      const removed = new Set([...newKeys, ...result.deleted]);
      setSamples((prev) => prev.filter((s) => !removed.has(String(s.SampleKey))));
      setSelectedSampleKeys([]);
      if (result.blocked.length > 0) {
        const copy = buildSampleCascadeCopy(result.blocked);
        const blockedKeys = result.blocked.map((b) => b.sampleKey);
        setConfirmDlg({
          open: true,
          title: copy.title,
          message: copy.message,
          confirmLabel: copy.confirmLabel,
          confirmColor: 'error',
          onConfirm: async () => {
            try {
              const retry = await deleteSamples(blockedKeys, { cascade: true });
              const gone = new Set(retry.deleted);
              setSamples((prev) => prev.filter((s) => !gone.has(String(s.SampleKey))));
              if (copy.scheduledCount > 0) {
                setSnackbar({
                  open: true,
                  message: `${retry.deleted.length} sample(s) + tests removed - ${copy.scheduledCount} scheduled task(s) now out of sync. Open the work order and run "Sync now".`,
                  severity: 'warning',
                });
              }
            } catch {
              setSnackbar({ open: true, message: 'Failed to delete samples', severity: 'error' });
            }
          },
        });
      }
    } catch { console.error('Failed to delete samples'); }
  };

  const hasUnsavedRows = samples.some((s) => String(s.SampleKey).startsWith('new-'));

  const handleLocationSelect = (key: string) => {
    if (hasUnsavedRows) {
      setConfirmDlg({
        open: true,
        title: 'Unsaved Changes',
        message: 'You have unsaved samples. Discard changes?',
        confirmLabel: 'Discard',
        confirmColor: 'warning',
        onConfirm: () => {
          setSelectedLocationKey(key);
          const loc = locations.find((l) => String(l.LocationKey) === key);
          if (loc) setSearchParams({ location: loc.LocationId }, { replace: true });
        },
      });
      return;
    }
    setSelectedLocationKey(key);
    const loc = locations.find((l) => String(l.LocationKey) === key);
    if (loc) setSearchParams({ location: loc.LocationId }, { replace: true });
  };

  const handleLocationCreated = (loc: Location) => {
    setLocations((prev) => [...prev, loc]);
    setSelectedLocationKey(String(loc.LocationKey));
  };

  const handleCsvImport = useCallback(async (rows: NormalizedSampleRow[], fileUnit: DepthUnit) => {
    if (!selectedLocationKey || !selectedLocation) return;
    // BUG-2026-0170 (Gigi 07-31): the file's unit is ASKED in the dialog and
    // depths are stored exactly as they appear in the CSV — never converted.
    // The answer becomes the project's unit designation.
    // FEAT-2026-0017: rows route to their own Location ID (created if new);
    // blank ids go to the selected location.
    const outcome = await importSamplesAcrossLocations({
      projectKey: project.ProjectKey, rows, selectedLocation, locations,
    });
    if (outcome.createdLocations.length > 0) {
      setLocations((prev) => [...prev, ...outcome.createdLocations]);
    }
    if (fileUnit !== unit) {
      try { await persistUnit(fileUnit); } catch { /* import succeeded; label update is best-effort */ }
    }
    const data = await getSamplesByLocation(selectedLocationKey);
    setSamples(data as unknown as Record<string, unknown>[]);
    setSnackbar({ open: true, ...describeImportOutcome(outcome) });
  }, [selectedLocationKey, selectedLocation, project.ProjectKey, locations, unit, persistUnit]);

  const handleCreateWorkOrder = () => {
    if (!projectId || selectedSampleKeys.length === 0) return;
    navigate(ROUTES.CREATE_WORK_ORDER.replace(':projectId', projectId), { state: { sampleKeys: selectedSampleKeys } });
  };

  if (loading) return null; // Parent already shows spinner for initial load

  return (
    <>
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 2 }}>
        <SidePanel
          title="Locations"
          items={locations.map((l) => ({ key: String(l.LocationKey), label: l.LocationId, sublabel: l.LocationType }))}
          selectedKey={selectedLocationKey}
          onSelect={handleLocationSelect}
          onAdd={canManageEntities ? () => setDialogOpen(true) : undefined}
          addLabel="+ New Location"
          searchPlaceholder="Search locations..."
        />
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {selectedLocation ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="h6" fontWeight={600}>
                  Samples in {selectedLocation.LocationId}
                </Typography>
                <ToggleButtonGroup size="small" exclusive value={unit}
                  onChange={(_e, v) => v && handleUnitChange(v as DepthUnit)}>
                  <ToggleButton value="m" sx={{ px: 1.5, py: 0.25, fontSize: '0.75rem' }}>Metric (m)</ToggleButton>
                  <ToggleButton value="ft" sx={{ px: 1.5, py: 0.25, fontSize: '0.75rem' }}>Imperial (ft)</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <SampleActionBar
                onNewSample={handleAddRow}
                onUndo={handleUndo}
                canUndo={!!undoSnapshot || hasUnsavedRows}
                onDelete={() => handleDelete(selectedSampleKeys)}
                onCreateWorkOrder={handleCreateWorkOrder}
                onUploadSample={() => setUploadOpen(true)}
                onGenerateRefs={handleGenerateRefs}
                hasSelection={selectedSampleKeys.length > 0}
                hideNewSample={!canManageEntities}
                hideUploadSample={!canManageEntities}
                hideGenerateRefs={!canManageEntities}
                hideDelete={!canManageEntities}
                endSlot={<ColumnSelector columns={columnVisibility} onToggle={toggleColumn} onReset={resetDefaults} />}
              />
              <EditableGrid
                columns={filteredColumns} data={displaySamples}
                onRowChange={handleRowChange} onRowSave={handleRowSave}
                onSelectionChange={setSelectedSampleKeys} keyField="SampleKey"
                allowAdd={false} onAddRow={handleAddRow}
                allowDelete={selectedSampleKeys.length > 0 && canManageEntities} onDelete={handleDelete}
                readOnly={!canManageEntities}
                enableColumnFilters
              />
            </>
          ) : (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {locations.length === 0 ? 'No locations yet. Click "+ New Location" to add one.' : 'Select a location to view samples.'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <NewLocationDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onCreated={handleLocationCreated} projectKey={project.ProjectKey} />
      <CsvUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onImport={handleCsvImport}
        defaultUnit={unit}
        hasExistingSamples={samples.length > 0}
        selectedLocationId={selectedLocation?.LocationId ?? ''}
        locations={locations}
      />
      <Snackbar open={snackbar.open} autoHideDuration={snackbar.severity === 'success' ? 2000 : 4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))} variant="filled" sx={{ minWidth: 200 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* Floating bulk update bar — rendered outside document flow.
          Admin-gated to match PUT /samples/bulk-update-field. */}
      {selectedSampleKeys.length > 0 && canManageEntities && (
        <BulkUpdateBar selectedCount={selectedSampleKeys.length} columns={filteredColumns} onApply={handleBulkUpdate} />
      )}
      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
    </>
  );
};
